/* eslint-disable no-magic-numbers */
import { ConfigData, TextResources } from '../config/StoreLocatorTextResources';
import { STORELOCATORCOMPONENT } from '../components/storelocator.component';
import { ROUTE } from './route.directions.service';
import { Loader } from '@googlemaps/js-api-loader';
import { MARKER } from './google.marker.service';
import { HELPER } from './helper.service';

const GOOGLE_MAP_API_KEY = 'AIzaSyBZDgOido2ozNzFU2SNDANw0eIVsRpdZNQ';

export class GoogleMapService {
  /**
   * Propertys
   */
  hasDownBeenPressed: boolean = false;
  googleMap: google.maps.Map;
  gmapService: Loader;

  /**
   * Initializes the map with specified options
   * https://developers.google.com/maps/documentation/javascript/events?hl=de
   */
  async initMap(): Promise<void> {
    try {
      // Set map options
      const mapOptions: google.maps.MapOptions = {
        center: {
          lat: Number(ConfigData.data['latitude']),
          lng: Number(ConfigData.data['longitude']),
        },
        mapId: 'DEMO_MAP_ID',
        maxZoom: ConfigData.data['singleLocations'] ? 17 : NaN,
        minZoom: 6,
        zoom: Number(ConfigData.data['mapzoom']),
        gestureHandling: 'cooperative',
        // scrollwheel: false,
        // mapTypeControl: false,
        // streetViewControl: false,
        // fullscreenControl: false,
        // rotateControl: false,
        disableDefaultUI: true,
      };

      this.gmapService = new Loader({ apiKey: GOOGLE_MAP_API_KEY, version: 'quarterly' });
      this.gmapService
        .importLibrary('maps')
        .then(({ Map }) => {
          const mapElement = STORELOCATORCOMPONENT.mapHtmlElement;

          if (mapElement) {
            this.googleMap = new Map(mapElement, mapOptions);

            MARKER.map = this.googleMap;
            ROUTE.map = this.googleMap;

            // Initialize controls
            this.googleMap.controls[google.maps.ControlPosition.TOP_RIGHT].push(this.createZoomControl());
            this.googleMap.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(this.createLocationControl());
            if (!ConfigData.data['singleLocations']) {
              this.googleMap.controls[google.maps.ControlPosition.TOP_LEFT].push(this.createSidebarControl());
            }
          } else {
            throw Error('no map div found');
          }
          //console.log('Google Maps API version: ' + google.maps.version);
          return this.googleMap;
        })
        .catch((e) => {
          console.log('Error initializing map: ', e);
          throw e;
        });
    } catch (error) {
      console.error('Error in initMap:', error);
    }
  }

  createZoomControl() {
    const zoomControlDiv = document.createElement('cwc-map-control');
    const zoomInButton = document.createElement('button');
    const zoomOutButton = document.createElement('button');

    zoomInButton.textContent = '+';
    zoomInButton.title = TextResources.resources['control'].zoom_in;
    zoomInButton.type = 'button';
    zoomInButton.className = 'zoom-control-in';
    zoomInButton.addEventListener('click', () => {
      this.googleMap.setZoom(this.googleMap.getZoom() + 1);
    });

    zoomOutButton.textContent = '–';
    zoomOutButton.title = TextResources.resources['control'].zoom_out;
    zoomOutButton.type = 'button';
    zoomOutButton.className = 'zoom-control-out';
    zoomOutButton.addEventListener('click', () => {
      if (
        STORELOCATORCOMPONENT.requestDistance >= 10 &&
        STORELOCATORCOMPONENT.requestDistance < 20 &&
        !ConfigData.data['singleLocations']
      ) {
        STORELOCATORCOMPONENT.requestDistance = STORELOCATORCOMPONENT.requestDistance + 5;
        STORELOCATORCOMPONENT.sendLocationRequest(STORELOCATORCOMPONENT.geoCoords);
      } else {
        this.googleMap.setZoom(this.googleMap.getZoom() - 1);
      }
    });

    zoomControlDiv.className = 'controls zoom-control';
    zoomControlDiv.appendChild(zoomInButton);
    zoomControlDiv.appendChild(zoomOutButton);

    return zoomControlDiv;
  }

  createLocationControl() {
    const locationControlDiv = document.createElement('cwc-map-control');
    const locationButton = document.createElement('button');

    locationButton.type = 'button';
    locationButton.className = 'custom-location-control icon-current-location';
    locationButton.addEventListener('click', () => {
      this.locationControlClickEvent();
    });

    locationControlDiv.className = 'controls location-control';
    locationControlDiv.appendChild(locationButton);

    return locationControlDiv;
  }

  createSidebarControl() {
    const sidebarButton = document.createElement('cwc-sidebar-slide');
    const sButton = document.createElement('button');
    sButton.type = 'button';
    sButton.className = 'pac-target-button icon-arrow-left';
    sButton.title = TextResources.resources['location'].control.widget_hide;
    sidebarButton.appendChild(sButton);

    sidebarButton.addEventListener('click', () => {
      const button = sidebarButton.firstChild;

      HELPER.toggleTwoClasses(button, 'icon-arrow-left', 'icon-arrow-right');
      HELPER.changeAttributeValue(
        button,
        'title',
        TextResources.resources['location'].control.widget_hide,
        TextResources.resources['location'].control.widget_show,
      );
      STORELOCATORCOMPONENT.sidebarHtmlElement.classList.toggle('slide');
      STORELOCATORCOMPONENT.mapHtmlElement.classList.toggle('slide');
    });

    if (ConfigData.data['freeSearch']) {
      sidebarButton.style.display = 'none';
    }

    return sidebarButton;
  }

  locationControlClickEvent() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const geoCoords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.googleMap.panTo(geoCoords);
          this.googleMap.setZoom(17);
          ROUTE.geoCoords = geoCoords;
          STORELOCATORCOMPONENT.geoCoords = geoCoords;
          MARKER.createMyLocationMarker(geoCoords);
        },
        () => {
          STORELOCATORCOMPONENT.displayNoticeOverlay(TextResources.resources['error'].geolocation_service);
        },
      );
    } else {
      // Browser doesn't support Geolocation
      STORELOCATORCOMPONENT.displayNoticeOverlay(TextResources.resources['error'].geolocation_support);
    }
  }

  /**
   * Initializes autocomplete functionality on a specified input element
   * https://developers.google.com/maps/documentation/javascript/place-autocomplete-data
   */
  async initAutocomplete(inputElement: HTMLInputElement, onPlaceSelected: CallableFunction) {
    if (!this.gmapService) {
      this.gmapService = new Loader({ apiKey: GOOGLE_MAP_API_KEY, version: 'quarterly' });
    }
    const { Autocomplete } = await this.gmapService.importLibrary('places');

    const countrycode = this.getCountrycode();
    const options = {
      componentRestrictions: { country: countrycode },
      fields: ['geometry', 'name'],
    };
    const autocomplete = new Autocomplete(inputElement, options);

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      let latitude = null;
      let longitude = null;
      let cityName = null;

      if (place.geometry) {
        latitude = place.geometry.location.lat();
        longitude = place.geometry.location.lng();
      }

      if (place.name) {
        cityName = place.name;
      }
      // Pass the extracted details to the callback function
      onPlaceSelected({ latitude, longitude, cityName });
      GOOGLEMAPSERVICE.hasDownBeenPressed = false;
    });

    // Enter key selects first entry from the location list.
    inputElement.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown') {
        GOOGLEMAPSERVICE.hasDownBeenPressed = true;
      }
      if (!GOOGLEMAPSERVICE.hasDownBeenPressed && event.key === 'Enter') {
        const keyArrowDownEvent = new KeyboardEvent('keydown', {
          key: 'ArrowDown',
          code: 'ArrowDown',
          keyCode: 40,
        });
        const keyEnterEvent = new KeyboardEvent('keydown', {
          key: 'Enter',
          code: 'Enter',
          keyCode: 13,
        });

        google.maps.event.trigger(this, 'keydown', keyArrowDownEvent);
        google.maps.event.trigger(this, 'keydown', keyEnterEvent);
      }
    });
  }

  getCountrycode() {
    let countrycode = ConfigData.data['countrycode'];
    countrycode = countrycode.substring(3);
    return countrycode;
  }
}

export const GOOGLEMAPSERVICE = new GoogleMapService();
